import {
  memberCardSummarySchema,
  type MemberCardSummary,
  type MemberLevel,
  type MemberOrigin,
} from '@growup/accounts-shared';
import { Button, Notice, PlayerCard, StatusBadge } from '@growup/ui';
import { useEffect, useRef, useState } from 'react';
import { z } from 'zod';

import { getJson, messageOf, request } from '../api';

/** Cartas por pedido: 6 filas de 8 num ecra grande. */
export const CARD_PAGE = 48;

/**
 * A foto so se pede quando a carta entra no ecra (com 2.800 contas, nunca
 * todas de uma vez). Sem foto visivel (ou de um menor para quem nao e
 * administrador) fica a silhueta.
 */
function useLazyPhoto(card: MemberCardSummary, target: React.RefObject<Element | null>) {
  const [url, setUrl] = useState<string | null>(null);
  const [near, setNear] = useState(false);

  useEffect(() => {
    const el = target.current;
    if (!card.photoVersion || !el) return;
    if (typeof IntersectionObserver === 'undefined') {
      setNear(true);
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setNear(true);
          io.disconnect();
        }
      },
      { rootMargin: '200px' },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [card.photoVersion, target]);

  useEffect(() => {
    if (!near || !card.photoVersion) return;
    let objectUrl: string | null = null;
    let live = true;
    const path = `/api/members/members/${card.accountId}/photo?v=${encodeURIComponent(card.photoVersion)}`;
    request('GET', path)
      .then((res) => res.blob())
      .then((blob) => {
        if (!live) return;
        objectUrl = URL.createObjectURL(blob);
        setUrl(objectUrl);
      })
      .catch(() => undefined);
    return () => {
      live = false;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [near, card.accountId, card.photoVersion]);

  return url;
}

export function GridCard({
  card,
  streamer,
  shirt,
  onOpen,
}: {
  card: MemberCardSummary;
  streamer: boolean;
  shirt: boolean;
  onOpen: (accountId: string) => void;
}) {
  const box = useRef<HTMLButtonElement>(null);
  const photoUrl = useLazyPhoto(card, box);
  return (
    <button
      ref={box}
      type="button"
      onClick={() => onOpen(card.accountId)}
      className="flex w-full flex-col items-center gap-2 rounded-md p-1 transition-transform hover:-translate-y-0.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gu-focus"
    >
      <PlayerCard
        name={card.login}
        tier={card.level}
        streamer={card.streamer}
        number={card.memberNumber}
        photoUrl={photoUrl}
        stats={[{ label: 'Anos', value: card.years }]}
        years={card.years}
        medals={card.awards.map((a) => ({ icon: a.icon, title: `${a.category} · ${a.edition}` }))}
        holo="hover"
        className="h-auto w-full"
      />
      {streamer || shirt ? (
        <span className="flex flex-wrap justify-center gap-1">
          {shirt ? (
            <StatusBadge tone="warning" symbol="★">
              1 ano de streamer
            </StatusBadge>
          ) : null}
          {streamer ? (
            <StatusBadge tone="info" symbol="▶">
              Streamer
            </StatusBadge>
          ) : null}
        </span>
      ) : null}
    </button>
  );
}

/**
 * As cartas ficam paradas e so a que esta debaixo do rato ganha o efeito
 * holografico (`holo="hover"`): com todas a brilhar a grelha arrastava-se.
 *
 * Os membros como cartas, por antiguidade (membro ha mais tempo primeiro), com
 * os mesmos filtros da tabela. Vem `CARD_PAGE` de cada vez; "Carregar mais"
 * junta a pagina seguinte.
 */
export function MemberCardGrid({
  query,
  level,
  origin,
  version,
  streamers,
  shirts,
  onOpen,
}: {
  query: string;
  level: MemberLevel | '';
  origin: MemberOrigin | '';
  version: number;
  streamers: ReadonlySet<string>;
  shirts: ReadonlySet<string>;
  onOpen: (accountId: string) => void;
}) {
  const [cards, setCards] = useState<MemberCardSummary[] | null>(null);
  const [offset, setOffset] = useState(0);
  const [more, setMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => setOffset(0), [query, level, origin, version]);

  useEffect(() => {
    const params = new URLSearchParams({ limit: String(CARD_PAGE), offset: String(offset) });
    if (query.trim()) params.set('q', query.trim());
    if (level) params.set('level', level);
    if (origin) params.set('origin', origin);
    let live = true;
    // Escrever na pesquisa nao deve fazer um pedido por tecla.
    const timer = setTimeout(() => {
      setLoading(true);
      getJson(`/api/members/members/cards?${params.toString()}`, z.array(memberCardSummarySchema))
        .then((rows) => {
          if (!live) return;
          setError(null);
          setCards((prev) => (offset === 0 || prev === null ? rows : [...prev, ...rows]));
          setMore(rows.length === CARD_PAGE);
        })
        .catch((e: unknown) => live && setError(messageOf(e)))
        .finally(() => live && setLoading(false));
    }, 250);
    return () => {
      live = false;
      clearTimeout(timer);
    };
  }, [query, level, origin, version, offset]);

  return (
    <div className="flex flex-col gap-4">
      {error ? <Notice tone="danger">{error}</Notice> : null}
      {cards === null ? (
        <p className="text-gu-ink-muted">A carregar…</p>
      ) : cards.length === 0 ? (
        <p className="text-gu-ink-muted">Ninguém encontrado.</p>
      ) : (
        <ul
          aria-label="Cartas dos membros, por antiguidade"
          className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 3xl:grid-cols-8"
        >
          {/* Fora do ecra a carta nao se pinta: «Carregar mais» nao vai pesando. */}
          {cards.map((c) => (
            <li
              key={c.accountId}
              className="[contain-intrinsic-height:auto_380px] [content-visibility:auto]"
            >
              <GridCard
                card={c}
                streamer={streamers.has(c.accountId)}
                shirt={shirts.has(c.accountId)}
                onOpen={onOpen}
              />
            </li>
          ))}
        </ul>
      )}
      {cards && more ? (
        <div className="flex justify-center">
          <Button variant="secondary" disabled={loading} onClick={() => setOffset(cards.length)}>
            {loading ? 'A carregar…' : 'Carregar mais'}
          </Button>
        </div>
      ) : null}
    </div>
  );
}
